import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TeamManagementService } from '../team-management.service';
import { AuthService } from '@core/auth.service';

interface QuarterChip {
  value: string;
  label: string;
}

const QUARTER_CHIPS: readonly QuarterChip[] = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Mid Year' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Year End' },
];

interface RatingOption {
  value: string;
  label: string;
}

/** Rating filter options — the same 8 buckets the tile chart uses, plus
 *  an "All" default. Values are the raw DB storage form so filtering
 *  matches whatever the BE returns. */
const RATING_OPTIONS: readonly RatingOption[] = [
  { value: '',        label: 'All Ratings' },
  { value: '5',       label: '5 - Exceptional' },
  { value: '4',       label: '4 - Exceeds Expectations' },
  { value: '3',       label: '3 - Meets Expectations' },
  { value: '2',       label: '2 - Partially Meets Expectations' },
  { value: '1',       label: '1 - Does Not Meet Expectations' },
  { value: 'TOO_NEW', label: 'NA - Too New to Rate' },
  { value: 'N_A',     label: 'NA - Not Applicable' },
  { value: 'UNRATED', label: 'Not Rated' },
];

interface FeedbackRow {
  employeeId: string;
  employeeName: string;
  reportingManagerName?: string;
  l3Name?: string;
  l4Name?: string;
  l5Name?: string;
  performanceRating?: string | null;
  comments?: string[];
  submittedDate?: string;
}

@Component({
  selector: 'app-team-performance-details',
  templateUrl: './team-performance-details.component.html',
  styleUrls: ['./team-performance-details.component.scss'],
})
export class TeamPerformanceDetailsComponent implements OnInit, OnDestroy {
  readonly quarterChips = QUARTER_CHIPS;
  readonly ratingOptions = RATING_OPTIONS;

  activeQuarter = 'Q2';
  activeYear = new Date().getFullYear();
  yearOptions: number[] = [];

  loading = false;
  loadError = false;
  rows: FeedbackRow[] = [];

  l3Filter = '';
  l4Filter = '';
  l5Filter = '';
  ratingFilter = '';

  /** Client-side pagination — table can grow into the hundreds once every
   *  active CWR under a leader shows up (LEFT JOIN includes Not Rated). */
  pageSize = 20;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  currentPage = 1;

  /** Contractor-profile modal state — opened when the manager clicks an
   *  employee row (Edit/Add Rating). Reuses <app-contractor-profile> so the
   *  full quarterly feedback form (with the current visible quarter,
   *  performance-rating dropdown, comments, etc.) is available inline. */
  showContractorProfileModal = false;
  selectedContractorId = '';

  private currentEmployeeId: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly teamService: TeamManagementService,
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    // Default to the previous completed quarter (Q wraps Q1 → Q4 of prior
    // year), matching the tile's default so navigation feels continuous.
    const now = new Date();
    const currentQ = Math.floor(now.getMonth() / 3) + 1;
    const prevQ = currentQ === 1 ? 4 : currentQ - 1;
    const prevYear = currentQ === 1 ? now.getFullYear() - 1 : now.getFullYear();
    this.activeQuarter = 'Q' + prevQ;
    this.activeYear = prevYear;
    this.yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

    // Prefer the dropdown selection if the user already picked a reportee
    // on the dashboard; fall back to the logged-in user's own employeeId
    // when the details page is opened directly (fresh navigation, no
    // dropdown state). Matches the pattern in team-composition-details.
    this.teamService
      .getSelectedDropdownValue()
      .pipe(takeUntil(this.destroy$))
      .subscribe((selection) => {
        if (selection?.employeeId) {
          this.currentEmployeeId = selection.employeeId;
          this.fetch();
        }
      });

    if (!this.currentEmployeeId) {
      this.authService.getEmployeeDetails().then((employeeData) => {
        if (this.currentEmployeeId) return; // dropdown selection landed first
        const selfId = employeeData?.employeeId;
        if (selfId) {
          this.currentEmployeeId = selfId;
          this.fetch();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    this.router.navigate(['/team-management']);
  }

  /** Open the contractor profile modal for the clicked row — that surface
   *  hosts the feedback form (dropdown + comments) so the manager can add
   *  or edit a rating without leaving the details page.
   *
   *  Also push a synthetic history entry so browser Back closes the modal
   *  instead of navigating away from the details page. Without this the
   *  Back button would skip past the modal (which never touched the URL)
   *  and land on the Team Dashboard — one of the pieces of navigation that
   *  the user flagged. */
  openContractorProfile(employeeId: string | undefined): void {
    if (!employeeId) return;
    this.selectedContractorId = employeeId;
    this.showContractorProfileModal = true;
    if (typeof history !== 'undefined') {
      history.pushState({ perfDetailsModal: true }, '');
    }
  }

  /** Catch the browser Back press while the modal is open — close the modal
   *  and stay on the details page instead of falling through to the previous
   *  route. Bound once at ngOnInit via `@HostListener` (see below). */
  @HostListener('window:popstate')
  onPopState(): void {
    if (this.showContractorProfileModal) {
      this.showContractorProfileModal = false;
      this.selectedContractorId = '';
      this.fetch();
    }
  }

  onContractorProfileClose(): void {
    if (this.showContractorProfileModal && typeof history !== 'undefined'
        && history.state && (history.state as { perfDetailsModal?: boolean }).perfDetailsModal) {
      // Pop the state we pushed on open so Back doesn't leave a stale
      // entry once the user explicitly closes via the modal's X.
      history.back();
      return; // popstate handler will finish the cleanup.
    }
    this.showContractorProfileModal = false;
    this.selectedContractorId = '';
    // After a rating edit, refetch so the table reflects the change without
    // requiring the user to re-open the page.
    this.fetch();
  }

  onQuarterChange(quarter: string): void {
    if (this.activeQuarter === quarter) return;
    this.activeQuarter = quarter;
    this.resetPage();
    this.fetch();
  }

  onYearChange(year: number | string): void {
    const y = typeof year === 'string' ? Number.parseInt(year, 10) : year;
    if (!y || y === this.activeYear) return;
    this.activeYear = y;
    this.resetPage();
    this.fetch();
  }

  onL3Change(): void {
    this.l4Filter = '';
    this.l5Filter = '';
    this.resetPage();
  }

  onL4Change(): void {
    this.l5Filter = '';
    this.resetPage();
  }

  onL5Change(): void {
    this.resetPage();
  }

  onRatingChange(): void {
    this.resetPage();
  }

  get l3Options(): string[] {
    return this.uniqueSorted((r) => r.l3Name);
  }

  get l4Options(): string[] {
    return this.uniqueSorted(
      (r) => r.l4Name,
      this.l3Filter ? (r) => r.l3Name === this.l3Filter : undefined,
    );
  }

  get l5Options(): string[] {
    return this.uniqueSorted(
      (r) => r.l5Name,
      (r) =>
        (!this.l3Filter || r.l3Name === this.l3Filter) &&
        (!this.l4Filter || r.l4Name === this.l4Filter),
    );
  }

  /** Map a raw rating value ("5"/"TOO_NEW"/"UNRATED"/etc.) to the full
   *  human-readable label used in the dropdown ("5 - Exceptional",
   *  "NA - Too New to Rate", "Not Rated"). Falls back to the raw value when
   *  unknown. Powers the Rating column in the table. */
  ratingLabel(raw?: string | null): string {
    const key = raw || 'UNRATED';
    const opt = RATING_OPTIONS.find((o) => o.value === key);
    return opt ? opt.label : key;
  }

  get filteredRows(): FeedbackRow[] {
    return this.rows.filter((r) => {
      const rating = (r.performanceRating || 'UNRATED') as string;
      const ratingMatch = !this.ratingFilter || rating === this.ratingFilter;
      return (
        ratingMatch &&
        (!this.l3Filter || r.l3Name === this.l3Filter) &&
        (!this.l4Filter || r.l4Name === this.l4Filter) &&
        (!this.l5Filter || r.l5Name === this.l5Filter)
      );
    });
  }

  /** Rows to render for the current page. Client-side slice of filteredRows;
   *  keeps DOM small when the manager has hundreds of reports. */
  get pagedRows(): FeedbackRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
  }

  get pageStart(): number {
    if (this.filteredRows.length === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredRows.length);
  }

  goToPage(page: number): void {
    const clamped = Math.min(Math.max(1, page), this.totalPages);
    if (clamped === this.currentPage) return;
    this.currentPage = clamped;
  }

  onPageSizeChange(size: number | string): void {
    const n = typeof size === 'string' ? Number.parseInt(size, 10) : size;
    if (!n || n === this.pageSize) return;
    this.pageSize = n;
    this.currentPage = 1;
  }

  /** Reset to page 1 whenever any filter changes so the user doesn't sit
   *  on an out-of-range page after narrowing the list. */
  private resetPage(): void {
    this.currentPage = 1;
  }

  clearFilters(): void {
    this.l3Filter = '';
    this.l4Filter = '';
    this.l5Filter = '';
    this.ratingFilter = '';
    this.resetPage();
  }

  get hasFilters(): boolean {
    return !!(this.l3Filter || this.l4Filter || this.l5Filter || this.ratingFilter);
  }

  private uniqueSorted(
    pick: (r: FeedbackRow) => string | undefined,
    filterFn?: (r: FeedbackRow) => boolean,
  ): string[] {
    const s = new Set<string>();
    for (const r of this.rows) {
      if (filterFn && !filterFn(r)) continue;
      const v = pick(r);
      if (v) s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }

  private fetch(): void {
    if (!this.currentEmployeeId) return;
    this.loading = true;
    this.loadError = false;
    this.rows = [];
    this.teamService
      .getQuarterlyFeedbackList(this.currentEmployeeId, this.activeYear, this.activeQuarter)
      .subscribe({
        next: (resp) => {
          this.loading = false;
          this.rows = (resp?.data as FeedbackRow[]) || [];
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
        },
      });
  }
}
